import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardMedia,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add,
  Delete,
  ArrowUpward,
  ArrowDownward,
  ExpandMore,
  Image as ImageIcon,
  ListAlt as SectionIcon,
  Quiz as QuizIcon,
} from '@mui/icons-material';

const AcademicQuestionPaperForm = ({
  initialData = null,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    subject: '',
    standard: '',
    maximumMarks: '',
    academicYear: new Date().getFullYear().toString(),
    examName: '',
    examDuration: '',
    teacherId: '',
    teacherName: '',
    masterId: '',
    masterCode: '',
    createdBy: '',
    questionPapers: [],
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        subject: initialData.subject || '',
        standard: initialData.standard || '',
        maximumMarks: initialData.maximumMarks || '',
        academicYear: initialData.academicYear || new Date().getFullYear().toString(),
        examName: initialData.examName || '',
        examDuration: initialData.examDuration || '',
        teacherId: initialData.teacherId || '',
        teacherName: initialData.teacherName || '',
        masterId: initialData.masterId || '',
        masterCode: initialData.masterCode || '',
        createdBy: initialData.createdBy || '',
        questionPapers: initialData.questionPapers || [],
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuestionChange = (index, field, value) => {
    const updatedQuestions = [...formData.questionPapers];
    updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
    setFormData((prev) => ({
      ...prev,
      questionPapers: updatedQuestions,
    }));
  };

  const addQuestion = (type = 'MCQ') => {
    const newQuestion = {
      questionNumber: formData.questionPapers.length + 1,
      question: '',
      questionType: type,
      optionA: '',
      optionB: '',
      optionC: '',
      optionD: '',
      correctOption: '',
      marks: 1,
      imageUrl: '',
      sectionName: '',
      instructions: '',
    };
    setFormData((prev) => ({
      ...prev,
      questionPapers: [...prev.questionPapers, newQuestion],
    }));
  };

  const removeQuestion = (index) => {
    const updated = formData.questionPapers.filter((_, i) => i !== index);
    // Renumber
    updated.forEach((q, i) => q.questionNumber = i + 1);
    setFormData((prev) => ({
      ...prev,
      questionPapers: updated,
    }));
  };

  const moveQuestionUp = (index) => {
    if (index === 0) return;
    const updated = [...formData.questionPapers];
    [updated[index], updated[index - 1]] = [updated[index - 1], updated[index]];
    updated.forEach((q, i) => q.questionNumber = i + 1);
    setFormData((prev) => ({ ...prev, questionPapers: updated }));
  };

  const moveQuestionDown = (index) => {
    if (index === formData.questionPapers.length - 1) return;
    const updated = [...formData.questionPapers];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    updated.forEach((q, i) => q.questionNumber = i + 1);
    setFormData((prev) => ({ ...prev, questionPapers: updated }));
  };

  const handleImageUpload = (index, file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      handleQuestionChange(index, 'imageUrl', reader.result);
    };
    if (file) {
      reader.readAsDataURL(file);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.standard.trim()) newErrors.standard = 'Standard is required';
    if (!formData.academicYear.trim()) newErrors.academicYear = 'Academic year is required';

    formData.questionPapers.forEach((q, index) => {
      if (!q.question.trim()) newErrors[`question_${index}`] = 'Question is required';
      if (q.questionType === 'MCQ') {
        if (!q.optionA?.trim()) newErrors[`optionA_${index}`] = 'Option A is required';
        if (!q.optionB?.trim()) newErrors[`optionB_${index}`] = 'Option B is required';
        if (!q.optionC?.trim()) newErrors[`optionC_${index}`] = 'Option C is required';
        if (!q.optionD?.trim()) newErrors[`optionD_${index}`] = 'Option D is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const submitData = {
        ...formData,
        maximumMarks: formData.maximumMarks ? Number(formData.maximumMarks) : null,
        questionPapers: formData.questionPapers.map((q) => ({
          ...q,
          marks: q.marks ? Number(q.marks) : 1,
        })),
      };
      onSubmit(submitData);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <form onSubmit={handleSubmit}>
        {/* Paper Header Info */}
        <Typography variant="h6" gutterBottom>
          <QuizIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Question Paper Details
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Exam Name"
              name="examName"
              value={formData.examName}
              onChange={handleChange}
              placeholder="e.g., Half Yearly Examination"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              error={!!errors.subject}
              helperText={errors.subject}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Standard"
              name="standard"
              value={formData.standard}
              onChange={handleChange}
              error={!!errors.standard}
              helperText={errors.standard}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Maximum Marks"
              name="maximumMarks"
              type="number"
              value={formData.maximumMarks}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Exam Duration"
              name="examDuration"
              value={formData.examDuration}
              onChange={handleChange}
              placeholder="e.g., 3 hours"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Academic Year"
              name="academicYear"
              value={formData.academicYear}
              onChange={handleChange}
              error={!!errors.academicYear}
              helperText={errors.academicYear}
              required
            />
          </Grid>
          {/* <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Created By"
              name="createdBy"
              value={formData.createdBy}
              onChange={handleChange}
            />
          </Grid> */}
        </Grid>

        {/* Questions Section */}
        <Box sx={{ mt: 4, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              <SectionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Questions ({formData.questionPapers.length})
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => addQuestion('MCQ')}
                size="small"
              >
                Add MCQ
              </Button>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => addQuestion('SHORT')}
                size="small"
              >
                Add Short Question
              </Button>
            </Stack>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {formData.questionPapers.map((question, index) => (
              <Accordion key={index} defaultExpanded={index === 0}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Chip
                      label={`Q${index + 1}`}
                      color="primary"
                      size="small"
                    />
                    <Chip
                      label={question.questionType}
                      color={question.questionType === 'MCQ' ? 'secondary' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                    <Typography sx={{ flex: 1, ml: 1 }} noWrap>
                      {question.question || 'New question...'}
                    </Typography>
                    <Chip label={`${question.marks || 1} mark(s)`} size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <IconButton
                      size="small"
                      onClick={() => moveQuestionUp(index)}
                      disabled={index === 0}
                    >
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => moveQuestionDown(index)}
                      disabled={index === formData.questionPapers.length - 1}
                    >
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                    <Box sx={{ flex: 1 }} />
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => removeQuestion(index)}
                    >
                      <Delete />
                    </IconButton>
                  </Box>

                  <Grid container spacing={2}>
                    {/* Section & Instructions (only for first question or grouped) */}
                    {index === 0 && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Section Name"
                            value={question.sectionName}
                            onChange={(e) =>
                              handleQuestionChange(index, 'sectionName', e.target.value)
                            }
                            placeholder="e.g., SECTION - A"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Instructions"
                            value={question.instructions}
                            onChange={(e) =>
                              handleQuestionChange(index, 'instructions', e.target.value)
                            }
                            placeholder="e.g., Q 1-20 are MCQ. Each question is 1 mark."
                          />
                        </Grid>
                      </>
                    )}

                    {/* Question Type & Marks */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Question Type</InputLabel>
                        <Select
                          value={question.questionType}
                          onChange={(e) =>
                            handleQuestionChange(index, 'questionType', e.target.value)
                          }
                          label="Question Type"
                        >
                          <MenuItem value="MCQ">Multiple Choice (MCQ)</MenuItem>
                          <MenuItem value="SHORT">Short Answer</MenuItem>
                          <MenuItem value="LONG">Long Answer</MenuItem>
                          <MenuItem value="TRUE_FALSE">True/False</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Marks"
                        type="number"
                        value={question.marks}
                        onChange={(e) =>
                          handleQuestionChange(index, 'marks', e.target.value)
                        }
                      />
                    </Grid>

                    {/* Question Text */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Question"
                        value={question.question}
                        onChange={(e) =>
                          handleQuestionChange(index, 'question', e.target.value)
                        }
                        error={!!errors[`question_${index}`]}
                        helperText={errors[`question_${index}`]}
                        multiline
                        rows={2}
                        required
                      />
                    </Grid>

                    {/* Image Upload */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<ImageIcon />}
                          size="small"
                        >
                          Upload Diagram
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) => handleImageUpload(index, e.target.files[0])}
                          />
                        </Button>
                        {question.imageUrl && (
                          <Box>
                            <img
                              src={question.imageUrl}
                              alt="Question diagram"
                              style={{ maxHeight: 80, maxWidth: 150, objectFit: 'contain' }}
                            />
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleQuestionChange(index, 'imageUrl', '')}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </Grid>

                    {/* MCQ Options */}
                    {question.questionType === 'MCQ' && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Options (mark the correct answer)
                          </Typography>
                        </Grid>
                        {['A', 'B', 'C', 'D'].map((opt) => {
                          const fieldKey = `option${opt}`;
                          const errorKey = `option${opt}_${index}`;
                          return (
                            <Grid item xs={12} sm={6} key={opt}>
                              <TextField
                                fullWidth
                                size="small"
                                label={`Option ${opt}`}
                                value={question[fieldKey] || ''}
                                onChange={(e) =>
                                  handleQuestionChange(index, fieldKey, e.target.value)
                                }
                                error={!!errors[errorKey]}
                                helperText={errors[errorKey]}
                                InputProps={{
                                  startAdornment: (
                                    <Radio
                                      checked={question.correctOption === opt}
                                      onChange={() =>
                                        handleQuestionChange(index, 'correctOption', opt)
                                      }
                                      size="small"
                                    />
                                  ),
                                }}
                              />
                            </Grid>
                          );
                        })}
                      </>
                    )}

                    {/* Short/Long Answer */}
                    {(question.questionType === 'SHORT' ||
                      question.questionType === 'LONG') && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Model Answer / Answer Key"
                          value={question.answer || ''}
                          onChange={(e) =>
                            handleQuestionChange(index, 'answer', e.target.value)
                          }
                          multiline
                          rows={2}
                        />
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {formData.questionPapers.length === 0 && (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography color="text.secondary">
                No questions added yet. Click "Add MCQ" or "Add Short Question" to get started.
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button onClick={onCancel} variant="outlined">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Saving...' : initialData ? 'Update' : 'Create'}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default AcademicQuestionPaperForm;